// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 — see ADDITIONAL-TERMS.md.

import type { ResolvedPalette, WaveStyle } from './types.ts';

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface FramePaint {
  width: number;
  height: number;
  palette: ResolvedPalette;
  style: WaveStyle;
  /** Instantaneous magnitudes (0..1) for the `bars` / `mirror` styles. */
  bars: Float32Array | null;
  /** Whole-clip peak envelope (0..1) for the `wave` style. */
  envelope: Float32Array | null;
  /** Playback position 0..1 (drives the `wave` sweep and the progress bar). */
  progress: number;
  caption: string;
  showProgressBar: boolean;
  background: ImageBitmap | null;
}

function fontStack(): string {
  return "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, Roboto, Helvetica, Arial, sans-serif";
}

function drawBackground(ctx: Ctx, p: FramePaint): void {
  const { width, height, palette, background } = p;
  if (background) {
    // cover-fit the image, then a scrim for legibility
    const ir = background.width / background.height;
    const fr = width / height;
    let dw = width;
    let dh = height;
    if (ir > fr) {
      dh = height;
      dw = height * ir;
    } else {
      dw = width;
      dh = width / ir;
    }
    ctx.drawImage(background, (width - dw) / 2, (height - dh) / 2, dw, dh);
    ctx.fillStyle = palette.scrim;
    ctx.fillRect(0, 0, width, height);
  } else {
    const g = ctx.createLinearGradient(0, 0, 0, height);
    g.addColorStop(0, palette.bg[0]);
    g.addColorStop(1, palette.bg[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);
  }
}

function barsGradient(ctx: Ctx, x0: number, x1: number, stops: string[]): CanvasGradient {
  const g = ctx.createLinearGradient(x0, 0, x1, 0);
  const n = stops.length;
  for (let i = 0; i < n; i++) g.addColorStop(n === 1 ? 0 : i / (n - 1), stops[i]);
  return g;
}

function roundedBar(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
  ctx.fill();
}

function wrapLines(ctx: Ctx, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const trial = line ? `${line} ${word}` : word;
    if (ctx.measureText(trial).width <= maxWidth || !line) {
      line = trial;
    } else {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  // Ellipsise if we ran out of room.
  if (words.join(' ') !== lines.join(' ') && lines.length > 0) {
    let last = lines[lines.length - 1];
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = `${last}…`;
  }
  return lines;
}

/** Paint one complete frame. Clears and redraws everything. */
export function drawFrame(ctx: Ctx, p: FramePaint): void {
  const { width, height, style } = p;
  ctx.clearRect(0, 0, width, height);
  drawBackground(ctx, p);

  const innerW = width * 0.84;
  const x0 = (width - innerW) / 2;
  const cy = height * 0.46;
  const bandHalf = height * 0.2;

  if (style === 'wave' && p.envelope) {
    drawWave(ctx, p, x0, innerW, cy, bandHalf);
  } else if (p.bars) {
    drawBars(ctx, p, x0, innerW, cy, bandHalf);
  }

  drawCaption(ctx, p);
  if (p.showProgressBar) drawProgressBar(ctx, p, x0, innerW);
}

function drawBars(
  ctx: Ctx,
  p: FramePaint,
  x0: number,
  innerW: number,
  cy: number,
  bandHalf: number,
): void {
  const bars = p.bars!;
  const n = bars.length;
  if (n === 0) return;
  const slot = innerW / n;
  const barW = Math.max(2, slot * 0.62);
  const radius = barW / 2;
  const grad = barsGradient(ctx, x0, x0 + innerW, p.palette.bars);
  ctx.fillStyle = grad;

  for (let i = 0; i < n; i++) {
    const x = x0 + i * slot + (slot - barW) / 2;
    const mag = bars[i];
    if (p.style === 'mirror') {
      const half = Math.max(barW / 2, mag * bandHalf);
      roundedBar(ctx, x, cy - half, barW, half * 2, radius);
    } else {
      const h = Math.max(barW, mag * bandHalf * 2);
      const baseline = cy + bandHalf;
      roundedBar(ctx, x, baseline - h, barW, h, radius);
    }
  }
}

function drawWave(
  ctx: Ctx,
  p: FramePaint,
  x0: number,
  innerW: number,
  cy: number,
  bandHalf: number,
): void {
  const env = p.envelope!;
  const n = env.length;
  if (n === 0) return;
  const slot = innerW / n;
  const barW = Math.max(1.5, slot * 0.7);
  const radius = barW / 2;
  const progressX = x0 + p.progress * innerW;
  const accent = barsGradient(ctx, x0, x0 + innerW, p.palette.bars);

  for (let i = 0; i < n; i++) {
    const cx = x0 + (i + 0.5) * slot;
    const half = Math.max(barW / 2, env[i] * bandHalf);
    ctx.fillStyle = cx <= progressX ? accent : p.palette.barsDim;
    roundedBar(ctx, cx - barW / 2, cy - half, barW, half * 2, radius);
  }

  // Playhead.
  ctx.fillStyle = p.palette.text;
  ctx.globalAlpha = 0.85;
  ctx.fillRect(progressX - 1, cy - bandHalf - 6, 2, bandHalf * 2 + 12);
  ctx.globalAlpha = 1;
}

function drawCaption(ctx: Ctx, p: FramePaint): void {
  const caption = p.caption.trim();
  if (!caption) return;
  const { width, height } = p;
  const fontSize = Math.round(width * (p.style === 'wave' ? 0.05 : 0.052));
  ctx.font = `700 ${fontSize}px ${fontStack()}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const maxWidth = width * 0.86;
  const lines = wrapLines(ctx, caption, maxWidth, 3);
  const lineH = fontSize * 1.22;
  const yStart = height * 0.76 - ((lines.length - 1) * lineH) / 2;

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = Math.round(fontSize * 0.35);
  ctx.fillStyle = p.palette.text;
  lines.forEach((ln, i) => ctx.fillText(ln, width / 2, yStart + i * lineH));
  ctx.restore();
}

function drawProgressBar(ctx: Ctx, p: FramePaint, x0: number, innerW: number): void {
  const y = p.height * 0.93;
  const h = Math.max(4, Math.round(p.height * 0.007));
  ctx.fillStyle = p.palette.barsDim;
  roundedBar(ctx, x0, y, innerW, h, h / 2);
  const grad = barsGradient(ctx, x0, x0 + innerW, p.palette.bars);
  ctx.fillStyle = grad;
  const w = Math.max(h, innerW * Math.min(1, Math.max(0, p.progress)));
  roundedBar(ctx, x0, y, w, h, h / 2);
}
